"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import VariationForm from "@/components/templates/VariationForm";
import {
  buildFormPageSchema,
  buildInitialData,
  normalizeCreationData,
  normalizeUpdateData,
} from "@/components/templates/VariationForm/form-data";
import { VariationFormData } from "@/components/templates/VariationForm/types";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useCreateProductVariation from "@/hooks/requests/product-variations/use-create-product-variation";
import useGetProductVariationById from "@/hooks/requests/product-variations/use-get-product-variation-by-id";
import useUpdateProductVariation from "@/hooks/requests/product-variations/use-update-product-variation";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useMutate from "@/hooks/use-mutate";
import { VariationFormPageMessages } from "@/messages/variation/variations-form-page.messages";

type Props = {
  messages: VariationFormPageMessages;
  isEditing?: boolean;
};

export default function VariationFormPage({ messages, isEditing = false }: Props) {
  const { variationId } = useParams();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const router = useNavigate();
  const { mutate } = useMutate();
  const customerId = useGetCustomerId();
  const _id = Number(variationId) || 0;

  const onSuccess = () => {
    mutate(SWR_KEYS.getProductVariationById);
    router(ROUTE_URLS.variations(lang));
  };

  const { create, isLoading: isLoadingCreate } = useCreateProductVariation();
  const { update, isLoading: isLoadingUpdate } = useUpdateProductVariation();

  const { data, isLoading, error } = useGetProductVariationById({ _id });

  const handleUpdate = async (_formData: VariationFormData) => {
    const formData = normalizeUpdateData(_formData);
    const successMessage = messages.updateSuccess;
    const errorMessage = messages.updateError;
    const _id = Number(variationId);

    update({ formData, successMessage, errorMessage, onSuccess, _id });
  };

  const handleCreate = async (_formData: VariationFormData) => {
    const formData = normalizeCreationData(_formData);
    const successMessage = messages.createSuccess;
    const errorMessage = messages.createError;

    create({ formData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmitForm = async (_formData: VariationFormData) => {
    if (!user) return;

    if (isEditing) handleUpdate(_formData);
    if (!isEditing) handleCreate(_formData);
  };

  const isLoadingSubmit = isEditing ? isLoadingUpdate : isLoadingCreate;
  const pageTitle = isEditing ? messages.editPageTitle : messages.createPageTitle;

  const shouldLoadForm = isEditing ? !!data && !error : true;
  const shouldLoadError = !data && !!error && !isLoading;

  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.variations(lang) }];

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoading}>
          {shouldLoadForm && (
            <Formik
              initialValues={buildInitialData(Number(customerId), isEditing, data)}
              validationSchema={buildFormPageSchema(messages)}
              validateOnChange={false}
              onSubmit={handleSubmitForm}
            >
              <VariationForm messages={messages} isLoading={isLoadingSubmit} />
            </Formik>
          )}
          {shouldLoadError && (
            <AlertBlock
              title={messages.errorToLoad}
              description={error?.message || ""}
              alt={messages.notFoundAlt}
            />
          )}

          {!data && !!error && (
            <AlertBlock
              title={messages.errorToLoad}
              description={error.message}
              alt={messages.notFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
